import React, { useState, useEffect, useMemo } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogClose } from "../../Components/UI/dialog";
import { Button } from "../../Components/UI/button";
import { Textarea } from "../../Components/UI/textarea";
import { Input } from "../../Components/UI/input";
import { Label } from "../../Components/UI/label";
import { X, Check, Search, Plus, Pencil, ArrowLeft, Trash2, ChevronDown, ChevronRight } from "lucide-react";
import { cn } from "../../Lib/Cn";
import useIsMobile from "../../Hooks/useIsMobile";
import { handleWhatsAppClickAction } from "../../Utils/ContactLogUtils";
import { toast } from "sonner";
import TemplateEditorDialog from "./TemplateEditorDialog";
import { saveMastersConfigAPI } from "../../Modules/Settings/MasterDataAPI";
import { getConfigData } from "../../Utils/Storage";
import defaultTemplates from "./defaultTemplates.json";
import { SpeechControls } from "./SpeechControls";

const TemplateDialog = ({ isOpen, onClose, connectionData, onRecordAdded, connID, stageID }) => {
    const [templates, setTemplates] = useState([]);
    const [selectedTemplateId, setSelectedTemplateId] = useState(null);
    const [message, setMessage] = useState("");
    const [isLoading, setIsLoading] = useState(false);
    const isMobile = useIsMobile();
    const [searchTerm, setSearchTerm] = useState("");
    const [isGeneralOpen, setIsGeneralOpen] = useState(false);

    // Editor modal state
    const [isEditorOpen, setIsEditorOpen] = useState(false);
    const [editingTemplate, setEditingTemplate] = useState(null);

    const activeStageID = stageID || connectionData?.currStageID || connectionData?.stageID;
    const targetStageType = activeStageID ? activeStageID + 1 : null;

    const fetchTemplates = () => {
        try {
            const configData = getConfigData();
            const tmps = configData?.WspTemplates;
            const data = typeof tmps === 'string' ? JSON.parse(tmps) : (tmps || []);
            const sortedData = data.sort((a, b) => b.TMID - a.TMID);
            setTemplates(sortedData);
            return data;
        } catch (e) {
            console.error("Failed to parse templates:", e);
            return [];
        }
    };

    useEffect(() => {
        if (isOpen) {
            fetchTemplates();
        }
    }, [isOpen]);

    const replacePlaceholders = (text) => {
        if (!text) return "";
        let ourName = "Our Team";
        let ourCompany = "Our Company";
        try {
            const rawJarvis = localStorage.getItem('Jarvis');
            if (rawJarvis) {
                const parsed = JSON.parse(rawJarvis);
                const user = parsed?.[0];
                if (user?.FirstName) {
                    ourName = `${user.FirstName} ${user.LastName || ''}`.trim();
                }
            }
            const rawAgency = localStorage.getItem('B');
            if (rawAgency) {
                const parsedAgency = JSON.parse(rawAgency);
                const activeAgn = Array.isArray(parsedAgency) ? parsedAgency[0] : parsedAgency;
                if (activeAgn?.AgencyName) {
                    ourCompany = activeAgn.AgencyName;
                }
            }
        } catch (e) {
            // fallback gracefully
        }
        return text
            .replace(/{{CONN_CONTACT_NAME}}/g, connectionData?.contactName || connectionData?.ContactName || "Customer")
            .replace(/{{CONN_COMPANY_NAME}}/g, connectionData?.bizName || connectionData?.BizName || "your company")
            .replace(/{{OUR_NAME}}/g, ourName)
            .replace(/{{OUR_COMPANY_NAME}}/g, ourCompany);
    };

    const { filteredStageTemplates, filteredGeneralTemplates } = useMemo(() => {
        const term = searchTerm.trim().toLowerCase();
        const combined = [...templates, ...defaultTemplates];
        const filtered = term
            ? combined.filter(t => t.TN?.toLowerCase().includes(term) || t.T?.toLowerCase().includes(term))
            : combined;

        return {
            filteredStageTemplates: filtered.filter(t => t.Type == targetStageType),
            filteredGeneralTemplates: filtered.filter(t => (t.Type || 1) == 1)
        };
    }, [templates, searchTerm, targetStageType]);

    const handleTemplateSelect = (template) => {
        setSelectedTemplateId(template.TMID);
        setMessage(replacePlaceholders(template.T));
    };

    const handleOpenEditor = (e, template = null) => {
        e.stopPropagation();
        setEditingTemplate(template);
        setIsEditorOpen(true);
    };

    const handleCloseEditor = () => {
        setIsEditorOpen(false);
        setEditingTemplate(null);
        fetchTemplates(); // Refresh templates after editing
    };

    const handleDeleteTemplate = async (e, tmid) => {
        if (e) {
            e.preventDefault();
            e.stopPropagation();
        }

        if (!tmid) return;

        setIsLoading(true);
        try {
            await saveMastersConfigAPI("templates", "delete", { TMID: tmid });
            await fetchTemplates();
            toast.success('Template Deleted.');
        } catch (error) {
            console.error("Failed to delete template", error);
            toast.error('Failed to delete template.');
        } finally {
            setIsLoading(false);
        }
    };

    const handleClose = () => {
        setSelectedTemplateId(null);
        setMessage("");
        setSearchTerm("");
        setIsGeneralOpen(false);
        onClose();
    };

    const handleInternalClick = (e) => {
        e.stopPropagation();
    };

    const renderHeader = () => (
        <DialogHeader>
            {isMobile && (
                <div className="sm:hidden flex items-center justify-between w-full h-8">
                    <div className="flex items-center gap-2 flex-1 min-w-0">
                        <DialogClose className="p-0 w-auto h-auto flex-shrink-0 flex justify-center">
                            <ArrowLeft className="h-6 w-6" onClick={handleClose} />
                            <span className="sr-only">Back</span>
                        </DialogClose>
                        <DialogTitle className="text-lg font-medium">
                            <div className="truncate">Send WhatsApp Message</div>
                        </DialogTitle>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                        <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8 p-0 hover:bg-white/10 hover:text-white"
                            onClick={(e) => handleOpenEditor(e)}
                        >
                            <Plus className="h-4 w-4" />
                        </Button>
                    </div>
                </div>
            )}
            {!isMobile && (
                <>
                    <div className="flex items-center gap-2">
                        <DialogTitle>Send WhatsApp Message</DialogTitle>
                    </div>

                    <div className="flex items-center gap-1">
                        <DialogClose asChild onClick={handleClose}>
                            <Button variant="ghost" size="icon" className="h-8 w-8 text-sub-text-color">
                                <X className="h-4 w-4" />
                                <span className="sr-only">Close</span>
                            </Button>
                        </DialogClose>
                    </div>
                </>
            )}
        </DialogHeader>
    );

    const renderTemplateCard = (template) => {
        const isActive = template.TMID === selectedTemplateId;

        return (
            <div
                key={template.TMID}
                onClick={() => handleTemplateSelect(template)}
                className={cn(
                    "relative cursor-pointer border rounded-md p-3 transition-all group sahi-radius",
                    isActive
                        ? "bg-white ring-gray-500 ring-1"
                        : "bg-white border-border-color hover:border-sahi-blue/50"
                )}
            >
                <div className="flex justify-between items-center gap-2">
                    {/* Left content */}
                    <div className="min-w-0 flex-1">
                        <h4 className="font-medium text-sm text-text-color mb-1 truncate">
                            {template.TN}
                        </h4>
                        <p className="text-xs text-sub-text-color line-clamp-2">
                            {template.T}
                        </p>
                    </div>

                    {/* Edit button */}
                    {!template.isStatic && (
                        <div className="flex gap-2 shrink-0">
                            <Button
                                variant="ghost"
                                size="icon"
                                className={cn(
                                    "h-6 w-6 flex items-center justify-center text-sahi-blue transition-opacity",
                                    isActive
                                        ? "opacity-100"
                                        : "opacity-0"
                                )}
                                onClick={(e) => handleOpenEditor(e, template)}
                            >
                                <Pencil className="h-3.5 w-3.5" />
                            </Button>
                            {templates.length > 1 && (
                                <Button
                                    variant="ghost"
                                    size="icon"
                                    className={cn(
                                        "h-6 w-6 flex items-center justify-center text-sahi-red transition-opacity",
                                        isActive
                                            ? "opacity-100"
                                            : "opacity-0"
                                    )}
                                    onClick={(e) => handleDeleteTemplate(e, template.TMID)}
                                >
                                    <Trash2 className="h-3.5 w-3.5" />
                                </Button>
                            )}
                        </div>
                    )}
                </div>
            </div>
        );
    };

    const renderList = () => (
        <div className={cn("flex overflow-hidden", isMobile ? "flex-col h-full" : "flex-row")}>
            {/* Right Side - Message Preview/Editor */}
            <div className={cn(
                "flex flex-col",
                isMobile ? "w-full flex-shrink-0" : "w-1/2 flex-1"
            )}>
                <div className="p-4 flex-1 flex flex-col">
                    <div className="mb-3">
                        <Label className="text-lg font-semibold text-color">
                            Message Preview
                        </Label>
                        <p className="text-xs text-muted-foreground">
                            To: {connectionData?.contactName || connectionData?.ContactName || "Customer"} ({connectionData?.bizName || connectionData?.BizName || "Company"})
                        </p>
                    </div>
                    <div className="relative flex-1 flex flex-col min-h-0">
                        <Textarea
                            id="template-message-preview"
                            value={message}
                            onChange={(e) => {
                                setMessage(e.target.value);
                                if (selectedTemplateId) setSelectedTemplateId(null);
                            }}
                            onClick={handleInternalClick}
                            onFocus={(e) => e.stopPropagation()}
                            placeholder="e.g., Hello..."
                            className={cn(
                                "flex-1 border-2 sahi-input transition-all text-sm sahi-radius overflow-y-auto pr-20",
                                isMobile ? "min-h-[80px] max-h-[30vh]" : "min-h-[200px] max-h-[44vh]"
                            )}
                            // You can keep this, but the max-h class above will override it when it gets too big
                            style={{ fieldSizing: "content" }}
                            maxLength={1500}
                        />
                        <SpeechControls
                            targetId="template-message-preview"
                            value={message}
                            onChange={(val) => {
                                setMessage(val);
                                if (selectedTemplateId) setSelectedTemplateId(null);
                            }}
                            isMobile={isMobile}
                        />
                    </div>
                    {message.length >= 450 && (
                        <span className="text-xs text-gray-500 mt-2">
                            {1500 - message.length} characters left
                        </span>
                    )}

                </div>
                <div>
                    {isMobile && renderMobileFooter()}
                </div>
            </div>

            {/* Left Side - Templates List */}
            <div className={cn(
                "flex flex-col border-l overflow-hidden",
                isMobile ? "w-full border-r-0 border-t flex-1 min-h-0" : "w-1/2"
            )}>
                {/* Sticky Header */}
                <div className="px-4 pt-4 sticky top-0 z-10">
                    <div className="flex justify-between items-center w-full pb-4">
                        <Label className="text-md font-semibold text-sub-text-color block">
                            Quick Templates
                        </Label>
                        {!isMobile ? (<Button
                            className="sahi-primary-button text-white gap-2"
                            onClick={(e) => handleOpenEditor(e)}
                        >
                            <>
                                <Plus className="h-4 w-4" strokeWidth={3} />
                                Add New
                            </>
                        </Button>) :
                            (<Button
                                variant="ghost"
                                size="sm"
                                className="text-muted-foreground px-0 hover:text-primary"
                                onClick={(e) => handleOpenEditor(e)}
                            >
                                +<span className="underline underline-offset-4">Add New</span>
                            </Button>)}
                    </div>
                    <div className="relative w-full">
                        <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-sub-text-color" />
                        <Input
                            placeholder="Search"
                            className="pl-9 h-9 sahi-input"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                    </div>
                </div>

                {/* Scrollable Templates */}
                <div className={cn(
                    "flex-1 overflow-y-auto p-4 gap-2 flex flex-col custom-scroll",
                    isMobile ? "flex-1 min-h-0" : "max-h-[350px] min-h-[350px]"
                )}>
                    {targetStageType ? (
                        filteredStageTemplates.length === 0 && filteredGeneralTemplates.length === 0 ? (
                            <div className="text-center py-8 text-sm text-sub-text-color">
                                {searchTerm ? "No templates found." : "No templates available."}
                            </div>
                        ) : (
                            <>
                                {/* Stage-Specific Templates */}
                                {filteredStageTemplates.length > 0 ? (
                                    <div className="flex flex-col gap-2">
                                        {filteredStageTemplates.map(renderTemplateCard)}
                                    </div>
                                ) : (
                                    <div className="text-center py-3 text-xs text-sub-text-color">
                                        {searchTerm ? "No stage-specific templates match your search." : "No stage-specific templates available."}
                                    </div>
                                )}

                                {/* Collapsible General Templates */}
                                <div className="mt-1 border-t pt-2">
                                    <button
                                        type="button"
                                        onClick={() => setIsGeneralOpen(prev => !prev)}
                                        className="flex items-center justify-between w-full py-1.5 px-2 text-xs font-semibold text-sub-text-color rounded transition-colors"
                                    >
                                        <div className="flex items-center gap-1.5">
                                            {isGeneralOpen || (searchTerm.trim().length > 0 && filteredGeneralTemplates.length > 0) ? (
                                                <ChevronDown className="h-3.5 w-3.5" />
                                            ) : (
                                                <ChevronRight className="h-3.5 w-3.5" />
                                            )}
                                            <span>General Templates</span>
                                        </div>
                                    </button>

                                    {(isGeneralOpen || (searchTerm.trim().length > 0 && filteredGeneralTemplates.length > 0)) && (
                                        <div className="flex flex-col gap-2 mt-2">
                                            {filteredGeneralTemplates.length === 0 ? (
                                                <div className="text-center py-2 text-xs text-sub-text-color">
                                                    No general templates found.
                                                </div>
                                            ) : (
                                                filteredGeneralTemplates.map(renderTemplateCard)
                                            )}
                                        </div>
                                    )}
                                </div>
                            </>
                        )
                    ) : (
                        filteredGeneralTemplates.length === 0 ? (
                            <div className="text-center py-8 text-sm text-sub-text-color">
                                {searchTerm ? "No templates found." : "No templates available."}
                            </div>
                        ) : (
                            filteredGeneralTemplates.map(renderTemplateCard)
                        )
                    )}
                </div>
            </div>
        </div>
    );

    const renderFooter = () => (
        <div className="p-4 border-t flex justify-start gap-3">
            <Button
                className="sahi-primary-button gap-2"
                onClick={(e) => {
                    e.stopPropagation();
                    handleClose();
                    handleWhatsAppClickAction(connectionData, onRecordAdded, connID, message);
                }}
                disabled={isLoading}
            >
                <>
                    <Check className="h-4 w-4" strokeWidth={3} />
                    Send Message
                </>
            </Button>
            <Button
                variant="outline"
                onClick={handleClose}
                className="sahi-secondary-button"
            >
                <X className="w-3 h-3" />
                Cancel
            </Button>
        </div>
    );

    const renderMobileFooter = () => (
        <div className="p-4 pt-0 flex justify-end gap-3">

            <Button
                className="sahi-primary-button gap-2"
                onClick={(e) => {
                    e.stopPropagation();
                    handleClose();
                    handleWhatsAppClickAction(connectionData, onRecordAdded, connID, message);
                }}
                disabled={isLoading}
            >
                <>
                    <Check className="h-4 w-4" strokeWidth={3} />
                    Send Message
                </>
            </Button>
        </div>
    );


    return (
        <>
            <Dialog open={isOpen} onOpenChange={handleClose}>
                <DialogContent
                    className={cn(
                        "p-0 gap-0 overflow-hidden flex flex-col sahi-radius",
                        isMobile
                            ? "max-h-[100vh] h-full w-full rounded-none"
                            : "sm:max-w-4xl h-[85vh]"
                    )}
                >
                    <div className="flex flex-col h-full" onClick={handleInternalClick}>
                        {renderHeader()}
                        <div className="flex-1 overflow-hidden relative">
                            {renderList()}
                        </div>
                        {!isMobile && renderFooter()}
                    </div>
                </DialogContent>
            </Dialog>

            {/* Separate Editor Dialog */}
            <TemplateEditorDialog
                isOpen={isEditorOpen}
                onClose={handleCloseEditor}
                template={editingTemplate}
                defaultType={targetStageType || 1}
            />
        </>
    );
};

export default TemplateDialog;